import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { assertWritable } from "@/lib/viewAs";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { PortalShell } from "@/components/layout/PortalShell";
import { CLIENT_NAV } from "@/lib/client-nav";
import { PortalPage } from "@/components/portal/PortalPage";
import { Phase2Card } from "@/components/portal/Phase2Card";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Shield } from "lucide-react";

export const Route = createFileRoute("/client/policies")({
  head: () => ({ meta: [{ title: "My Policies — Command Center" }, { name: "robots", content: "noindex" }] }),
  component: () => (
    <ProtectedRoute requiredRole="client">
      <PortalShell title="Family Command Center" nav={CLIENT_NAV} tourKey="client_tour">
        <PoliciesPage />
      </PortalShell>
    </ProtectedRoute>
  ),
});

interface Policy {
  id: string; carrier: string | null; product_type: string | null;
  policy_number: string | null; face_amount: number | null;
  premium: number | null; status: string; effective_date: string | null;
}

function fmtMoney(v: number | null) {
  return v == null ? "—" : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(v);
}

function PoliciesPage() {
  const { user } = useAuth();
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ carrier: "", product_type: "", policy_number: "", face_amount: "", premium: "" });

  async function load() {
    if (!user) return;
    setLoading(true);
    const { data: client } = await supabase.from("clients").select("id").eq("user_id", user.id).maybeSingle();
    if (!client) { setPolicies([]); setLoading(false); return; }
    const { data } = await supabase.from("policies").select("id,carrier,product_type,policy_number,face_amount,premium,status,effective_date").eq("client_id", client.id).order("created_at", { ascending: false });
    setPolicies((data ?? []) as Policy[]);
    setLoading(false);
  }
  useEffect(() => { void load(); }, [user?.id]);

  async function submit() {
    if (!assertWritable()) return;
    if (!user) return;
    const { data: client } = await supabase.from("clients").select("id").eq("user_id", user.id).maybeSingle();
    if (!client) { toast.error("Client record not found. Contact your Strategist."); return; }
    const insert = {
      client_id: client.id,
      carrier: form.carrier || null,
      product_type: form.product_type || null,
      policy_number: form.policy_number || null,
      face_amount: form.face_amount ? Number(form.face_amount) : null,
      premium: form.premium ? Number(form.premium) : null,
      status: "pending" as const,
    };
    const { error, data } = await supabase.from("policies").insert(insert).select("id").maybeSingle();
    if (error) { toast.error(error.message); return; }
    await supabase.from("activity_logs").insert({
      user_id: user.id, action: "policy.added", target_type: "policy",
      target_id: data?.id, metadata: { source: "client_portal" },
    });
    toast.success("Policy submitted for review");
    setOpen(false);
    setForm({ carrier: "", product_type: "", policy_number: "", face_amount: "", premium: "" });
    void load();
  }

  return (
    <PortalPage
      eyebrow="Household Protection"
      title="Your policies"
      intro="A living record of every policy protecting your household. Adding a policy notifies your Strategist for review."
      actions={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><PrimaryButton>Add Policy</PrimaryButton></DialogTrigger>
          <DialogContent className="bg-cream-50">
            <DialogHeader><DialogTitle className="font-display text-navy-800">Add a policy</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Carrier</Label><Input value={form.carrier} onChange={e => setForm({...form, carrier: e.target.value})} placeholder="e.g. F&G, Mutual of Omaha" /></div>
              <div><Label>Product type</Label><Input value={form.product_type} onChange={e => setForm({...form, product_type: e.target.value})} placeholder="IUL, Whole Life, Term, Annuity…" /></div>
              <div><Label>Policy number</Label><Input value={form.policy_number} onChange={e => setForm({...form, policy_number: e.target.value})} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Face amount ($)</Label><Input type="number" value={form.face_amount} onChange={e => setForm({...form, face_amount: e.target.value})} /></div>
                <div><Label>Annual premium ($)</Label><Input type="number" value={form.premium} onChange={e => setForm({...form, premium: e.target.value})} /></div>
              </div>
              <PrimaryButton onClick={submit} className="w-full">Submit for review</PrimaryButton>
            </div>
          </DialogContent>
        </Dialog>
      }
    >
      {loading ? <p className="font-sans text-charcoal-500">Loading…</p> :
        policies.length === 0 ? (
          <div className="bg-white border border-bronze-600/20 rounded-[4px] p-10 text-center shadow-soft">
            <Shield className="w-10 h-10 text-bronze-600 mx-auto" />
            <h3 className="mt-4 font-display text-navy-800 text-[22px]">No policies on record yet</h3>
            <p className="mt-2 font-sans text-[14px] text-charcoal-500">Add an existing policy or speak with your Strategist about coverage.</p>
          </div>
        ) : (
          <div className="bg-white border border-bronze-600/20 rounded-[4px] shadow-soft overflow-x-auto">
            <table className="w-full">
              <thead className="bg-cream-50 border-b border-bronze-600/30">
                <tr className="text-left font-sans text-[10px] uppercase tracking-[0.18em] text-bronze-600">
                  <th className="p-4">Carrier</th><th className="p-4">Type</th><th className="p-4">Policy #</th>
                  <th className="p-4">Face</th><th className="p-4">Premium</th><th className="p-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {policies.map(p => (
                  <tr key={p.id} className="border-b border-bronze-600/15 last:border-0 hover:bg-cream-50/60">
                    <td className="p-4 font-display text-navy-800">
                      <Link to="/client/policies/$policyId" params={{ policyId: p.id }} className="hover:text-bronze-600">
                        {p.carrier ?? "—"}
                      </Link>
                    </td>
                    <td className="p-4 font-sans text-[14px]">{p.product_type ?? "—"}</td>
                    <td className="p-4 font-mono text-[12px] text-charcoal-500">{p.policy_number ?? "—"}</td>
                    <td className="p-4 font-display text-navy-800">{fmtMoney(p.face_amount)}</td>
                    <td className="p-4 font-sans text-[14px]">{fmtMoney(p.premium)}</td>
                    <td className="p-4"><span className="px-2 py-1 rounded text-[11px] uppercase tracking-wider bg-bronze-400/20 text-navy-800">{p.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      }
      <div className="mt-10">
        <Phase2Card title="Annual Statement PDF" message="A year-end PDF summary of your coverage, contributions, and stewardship milestones will be available here at the close of each policy year." />
      </div>
    </PortalPage>
  );
}
